import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiEdit2, FiTrash2, FiPlus, FiX, FiImage } from "react-icons/fi";

const emptyForm = {
  title: "", category: "", description: "", aboutItem: "", info: "", price: "",
  platform: "", buyLink: "", buyLink2: "",
  style: "", size: "", itemCode: "",
  mainImage: "", images: [],
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const snap = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
      setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const catSnap = await getDocs(collection(db, "productCategories"));
      setCategories(catSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e, isMain = false) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const base64Arr = await Promise.all(files.map((file) => fileToBase64(file)));
      if (isMain) {
        setForm((f) => ({ ...f, mainImage: base64Arr[0] }));
      } else {
        setForm((f) => ({ ...f, images: [...f.images, ...base64Arr] }));
      }
    } catch (e) { alert("Upload failed."); }
    setUploading(false);
  };

  const removeGalleryImage = (url) => {
    setForm((f) => ({ ...f, images: f.images.filter((i) => i !== url) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price) || 0 };
      if (editId) {
        await updateDoc(doc(db, "products", editId), { ...data, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "products"), { ...data, createdAt: serverTimestamp() });
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditId(null);
      fetchData();
    } catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const handleEdit = (product) => {
    setForm({
      title: product.title || "",
      category: product.category || "",
      description: product.description || "",
      aboutItem: product.aboutItem || "",
      info: product.info || "",
      price: product.price || "",
      platform: product.platform || "",
      buyLink: product.buyLink || "",
      buyLink2: product.buyLink2 || "",
      style: product.style || "",
      size: product.size || "",
      itemCode: product.itemCode || "",
      mainImage: product.mainImage || "",
      images: product.images || [],
    });
    setEditId(product.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    setDeleting(id);
    await deleteDoc(doc(db, "products", id));
    setDeleting(null);
    fetchData();
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Products</h1>
            <p className="text-sm text-gray-400">Manage your shop products</p>
          </div>
          <button
            onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#e84fa5] text-white font-bold uppercase text-[13px] rounded-lg hover:bg-pink-600 transition"
          >
            <FiPlus /> Add Product
          </button>
        </div>

        {/* Product List */}
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div></div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Product</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Category</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Price</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Platform</th>
                  <th className="text-right px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-10 text-gray-400 text-sm">No products yet. Add your first product!</td></tr>
                )}
                {products.map((p) => (
                  <tr key={p.id} className="border-b hover:bg-gray-50 transition">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {(p.mainImage || (p.images && p.images[0])) && (
                          <img src={p.mainImage || p.images[0]} alt="" className="w-10 h-10 object-cover rounded-lg" />
                        )}
                        <span className="font-semibold text-[13px] text-black">{p.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[13px] text-gray-500">{p.category}</td>
                    <td className="px-5 py-4 text-[13px] font-bold">${p.price}</td>
                    <td className="px-5 py-4 text-[13px] text-gray-500">{p.platform || "—"}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(p)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition"><FiEdit2 size={15} /></button>
                        <button onClick={() => handleDelete(p.id)} disabled={deleting === p.id} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition">
                          {deleting === p.id ? <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div> : <FiTrash2 size={15} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-lg font-extrabold">{editId ? "Edit Product" : "Add Product"}</h2>
                <button onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }} className="text-gray-400 hover:text-gray-700"><FiX size={20} /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                {/* Main Image */}
                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-2">Main Product Image</label>
                  {form.mainImage && (
                    <div className="relative inline-block mb-2">
                      <img src={form.mainImage} alt="" className="w-24 h-24 object-cover rounded-lg border" />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, mainImage: "" }))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"><FiX /></button>
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
                    <FiImage /> {uploading ? "Uploading..." : "Upload Main Image"}
                    <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, true)} className="hidden" disabled={uploading} />
                  </label>
                </div>

                {/* Gallery Images */}
                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-2">Image Gallery (select multiple)</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative">
                        <img src={img} alt="" className="w-16 h-16 object-cover rounded-lg border" />
                        <button type="button" onClick={() => removeGalleryImage(img)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"><FiX /></button>
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
                    <FiImage /> {uploading ? "Uploading..." : "Add Gallery Images"}
                    <input type="file" accept="image/*" multiple onChange={(e) => handleImageUpload(e, false)} className="hidden" disabled={uploading} />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Product Title *</label>
                    <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Product name" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Category</label>
                    <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]">
                      <option value="">Select category</option>
                      {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-none" placeholder="Short description" />
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">About This Item</label>
                  <textarea value={form.aboutItem} onChange={(e) => setForm((f) => ({ ...f, aboutItem: e.target.value }))} rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-none" placeholder="About this item text shown after price" />
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Product Info / Details</label>
                  <textarea value={form.info} onChange={(e) => setForm((f) => ({ ...f, info: e.target.value }))} rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-none" placeholder="Materials, dimensions, care instructions, etc." />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Price (USD) *</label>
                    <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Platform (e.g. Amazon)</label>
                    <input value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Amazon, Etsy, etc." />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Style</label>
                    <input value={form.style} onChange={(e) => setForm((f) => ({ ...f, style: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="e.g. Modern" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Size</label>
                    <input value={form.size} onChange={(e) => setForm((f) => ({ ...f, size: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="e.g. 1 Oz" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Item Code</label>
                    <input value={form.itemCode} onChange={(e) => setForm((f) => ({ ...f, itemCode: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="e.g. SKU-001" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Amazon Buy Link</label>
                    <input type="url" value={form.buyLink} onChange={(e) => setForm((f) => ({ ...f, buyLink: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="https://amazon.com/dp/..." />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">eBuy / Other Buy Link</label>
                    <input type="url" value={form.buyLink2} onChange={(e) => setForm((f) => ({ ...f, buyLink2: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="https://ebay.com/itm/..." />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }}
                    className="px-5 py-2.5 border border-gray-200 rounded-lg text-[13px] font-semibold hover:bg-gray-50 transition">Cancel</button>
                  <button type="submit" disabled={saving || uploading}
                    className="px-6 py-2.5 bg-[#e84fa5] text-white font-bold text-[13px] rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
                    {saving ? "Saving..." : editId ? "Update Product" : "Add Product"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Products;
